import styles from './DevicesSelector.module.scss'

import { useMemo, useState } from 'react'

import { orderSystemsGroupType } from 'common/functions/orderSystems'
import { Checkbox } from '../../Atoms/Checkbox/Checkbox'
import { getMicaTypeLabel } from 'common/adapters/translations/getMicaTypeLabel'
import CollapseChevron from 'common/components/CollapseChevron/CollapseChevron'

const SELECT_ALL = 'SELECT_ALL'

const GROUPING_MODES = {
  GROUP: 'group',
  TYPE: 'type'
}

export const DeviceSelector = ({
  translate,
  selectedCheckboxIds,
  setSelectedCheckboxIds,
  systems,
  userWritting,
  checkboxColor,
  grouping_mode: groupingMode = GROUPING_MODES.GROUP,
  selectAllInfoButton = null,
  selectAllLabel = translate('selectAll')
}) => {
  const [open, setOpen] = useState({})

  // Build the render tree from the selected mode:
  // group: group -> type -> devices
  // type: type -> devices
  const systemsTree = useMemo(() => {
    if (groupingMode === GROUPING_MODES.TYPE) {
      // Type mode is used when the caller does not want the project/group layer
      // rendered above each device type.
      return systems.reduce((systemsByType, system) => {
        const type = system.micaType
        return {
          ...systemsByType,
          [type]: [...(systemsByType[type] || []), system]
        }
      }, {})
    }

    const grouped = orderSystemsGroupType(systems)
    const { undefined: unassignedSystems, ...assignedSystems } = grouped

    // Group mode keeps the legacy behavior: devices without a group are shown
    // under the translated "No group" bucket.
    if (!unassignedSystems) {
      return assignedSystems
    }

    return {
      ...assignedSystems,
      [translate('noGroup')]: unassignedSystems
    }
  }, [groupingMode, systems, translate])

  const handleClick = key => {
    const openKey = open[key]
    setOpen({ ...open, [key]: !openKey })
  }

  const isChecked = id => selectedCheckboxIds.includes(id)

  const getTypeKey = type => `type-${type}`

  const systemIdSet = useMemo(() => new Set(systems.map(system => system._id)), [systems])

  // The selected devices are the source of truth. We always calculate the
  // parent (group/type) and "Select all" checkboxes from them. A parent is
  // checked when all of its devices are checked, no matter how the user did it.
  const selectedDeviceIds = selectedCheckboxIds.filter(id => systemIdSet.has(id))

  const deriveSelection = deviceIds => {
    const deviceSet = new Set(deviceIds)
    const isBranchFull = ids => ids.length > 0 && ids.every(id => deviceSet.has(id))
    const parentIds = []

    if (groupingMode === GROUPING_MODES.TYPE) {
      Object.keys(systemsTree).forEach(type => {
        if (isBranchFull(systemsTree[type].map(system => system._id))) {
          parentIds.push(getTypeKey(type))
        }
      })
    } else {
      Object.keys(systemsTree).forEach(group => {
        const typeMap = systemsTree[group]
        const types = Object.keys(typeMap)
        const fullTypes = types.filter(type =>
          isBranchFull(typeMap[type].map(system => system._id))
        )
        parentIds.push(...fullTypes.map(type => `${group}-${type}`))
        if (types.length > 0 && fullTypes.length === types.length) {
          parentIds.push(group)
        }
      })
    }

    const allSelected = isBranchFull([...systemIdSet])
    return [...(allSelected ? [SELECT_ALL] : []), ...parentIds, ...deviceIds]
  }

  // Turn a branch on or off. If all its ids are already selected, remove them.
  // If not, add them. Then calculate the parent checkboxes from the result.
  const toggleDeviceIds = ids => {
    const allSelected = ids.length > 0 && ids.every(id => selectedDeviceIds.includes(id))
    const nextDeviceIds = allSelected
      ? selectedDeviceIds.filter(id => !ids.includes(id))
      : [...new Set([...selectedDeviceIds, ...ids])]
    return deriveSelection(nextDeviceIds)
  }

  const onSelectAllSystemsClick = () => {
    if (isChecked(SELECT_ALL)) {
      setSelectedCheckboxIds([])
      return
    }
    setSelectedCheckboxIds(deriveSelection([...systemIdSet]))
  }

  const onSelectTypeClick = typeSystems => {
    setSelectedCheckboxIds(toggleDeviceIds(typeSystems.map(system => system._id)))
  }

  const onSelectSystemClick = systemId => {
    setSelectedCheckboxIds(toggleDeviceIds([systemId]))
  }

  const onSelectGroupClick = systemsByType => {
    const groupSystemIds = Object.values(systemsByType)
      .flat()
      .map(system => system._id)
    setSelectedCheckboxIds(toggleDeviceIds(groupSystemIds))
  }

  const renderSystem = system => (
    <div
      key={system._id}
      className={styles.micaItem}
    >
      <Checkbox
        value={system._id}
        label={system.name}
        onChange={() => onSelectSystemClick(system._id)}
        isChecked={isChecked(system._id)}
        color={checkboxColor}
      />
    </div>
  )

  const renderType = (type, typeSystems, groupKey) => {
    // In group mode the group name is part of the type key to avoid collisions
    // when different groups contain the same device type.
    const typeKey = groupKey ? `${groupKey}-${type}` : getTypeKey(type)

    return (
      <div
        key={typeKey}
        className={styles.typeListContainer}
      >
        <div className={styles.typeListItem}>
          <button
            type='button'
            onClick={() => handleClick(typeKey)}
            className={styles.chevronButton}
          >
            <CollapseChevron expanded={userWritting ? true : open[typeKey]} />
          </button>
          <Checkbox
            value={typeKey}
            label={getMicaTypeLabel(type, translate)}
            onChange={() => onSelectTypeClick(typeSystems)}
            isChecked={isChecked(typeKey)}
            color={checkboxColor}
          />
        </div>
        <div
          style={(userWritting ? true : open[typeKey]) ? { display: 'block' } : { display: 'none' }}
        >
          {typeSystems.map(system => renderSystem(system))}
        </div>
      </div>
    )
  }

  const renderGroupMode = () => (
    <div className={styles.groupsContainer}>
      {Object.keys(systemsTree).map(group => (
        <div key={group}>
          <div className={styles.groupListItem}>
            <button
              type='button'
              onClick={() => handleClick(group)}
              className={styles.chevronButton}
            >
              <CollapseChevron expanded={userWritting ? true : open[group]} />
            </button>
            <Checkbox
              value={group}
              label={group}
              onChange={() => onSelectGroupClick(systemsTree[group])}
              isChecked={isChecked(group)}
              color={checkboxColor}
            />
          </div>
          <div
            style={(userWritting ? true : open[group]) ? { display: 'block' } : { display: 'none' }}
          >
            {Object.keys(systemsTree[group]).map(type =>
              renderType(type, systemsTree[group][type], group)
            )}
          </div>
        </div>
      ))}
    </div>
  )

  const renderTypeMode = () => (
    <div className={styles.groupsContainer}>
      {Object.keys(systemsTree).map(type => renderType(type, systemsTree[type]))}
    </div>
  )

  return (
    <>
      {systems.length > 0 && (
        <div
          className={styles.selectAllContainer}
          key={'selectAllContainer'}
        >
          <Checkbox
            value={SELECT_ALL}
            label={selectAllLabel}
            onChange={onSelectAllSystemsClick}
            isChecked={isChecked(SELECT_ALL)}
            color={checkboxColor}
          />
          {selectAllInfoButton}
        </div>
      )}
      <div className={styles.deviceListContent}>
        {groupingMode === GROUPING_MODES.TYPE ? renderTypeMode() : renderGroupMode()}
      </div>
    </>
  )
}
